import * as z from 'zod';

import { envVarsSchema } from './types.ts';

/**
 * Env vars must be strings, so we convert them to booleans in code.
 */
const stringToBool = z
  .enum(['true', 'false'])
  .default('false')
  .transform((value) => value === 'true');

/**
 * Secrets must never be shared with the browser, or committed to code.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file (please ensure gitignore).
 */
const baseSecretsSchema = z.object({
  CONTENTFUL_SPACE_ID: z.string(),
  CONTENTFUL_ACCESS_TOKEN: z.string(),
  CONTENTFUL_PERSONAL_ACCESS_TOKEN: z.string(),
  CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string(),
  CONTENTFUL_REVALIDATE_SECRET: z.string(),
  CONTENTFUL_PREVIEW_SECRET: z.string(),
  CONTENTFUL_ENVIRONMENT_ID: z.string(),
  CONTENTFUL_EDITOR_APP_ID: z.string(),
  REDIRECT_API_URL: z.string(),
  REDIRECT_API_KEY: z.string(),
  SIMPLESTATS_API_URL: z.string().optional(),
  SIMPLESTATS_API_KEY: z.string().optional(),
  SIMPLESTATS_CLIENT_ID: z.string().optional(),
  OPTIMIZELY_SDK_KEY: z.string().optional(),
  JWT_ISSUER_LIST: z
    .string()
    .default('[]')
    .describe('A JSON array of valid JWT issuers.'),
});

const editorSecretsSchema = baseSecretsSchema.extend({
  CAMPAIGNS_API_URL: z.string(),
  CAMPAIGNS_API_KEY: z.string(),
  CONTENTFUL_SIGNING_SECRET: z.string().optional(),
  TENOVOS_API_URL: z.string().optional(),
  TENOVOS_API_KEY: z.string().optional(),
  TENOVOS_CLIENT_ID: z.string().optional(),
  TENOVOS_USER: z.string().optional(),
  TENOVOS_PASSWORD: z.string().optional(),
});

const getBaseSecretValues = () => ({
  CONTENTFUL_SPACE_ID: process.env.CONTENTFUL_SPACE_ID,
  CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
  CONTENTFUL_PERSONAL_ACCESS_TOKEN:
    process.env.CONTENTFUL_PERSONAL_ACCESS_TOKEN,
  CONTENTFUL_PREVIEW_ACCESS_TOKEN: process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
  CONTENTFUL_REVALIDATE_SECRET: process.env.CONTENTFUL_REVALIDATE_SECRET,
  CONTENTFUL_PREVIEW_SECRET: process.env.CONTENTFUL_PREVIEW_SECRET,
  CONTENTFUL_ENVIRONMENT_ID: process.env.CONTENTFUL_ENVIRONMENT_ID,
  CONTENTFUL_EDITOR_APP_ID: process.env.CONTENTFUL_EDITOR_APP_ID,
  REDIRECT_API_URL: process.env.REDIRECT_API_URL,
  REDIRECT_API_KEY: process.env.REDIRECT_API_KEY,
  SIMPLESTATS_API_URL: process.env.SIMPLESTATS_API_URL,
  SIMPLESTATS_API_KEY: process.env.SIMPLESTATS_API_KEY,
  SIMPLESTATS_CLIENT_ID: process.env.SIMPLESTATS_CLIENT_ID,
  JWT_ISSUER_LIST: process.env.JWT_ISSUER_LIST,
  OPTIMIZELY_SDK_KEY: process.env.OPTIMIZELY_SDK_KEY,
});

const getEditorSecretValues = () => ({
  ...getBaseSecretValues(),
  CAMPAIGNS_API_URL: process.env.CAMPAIGNS_API_URL,
  CAMPAIGNS_API_KEY: process.env.CAMPAIGNS_API_KEY,
  CONTENTFUL_SIGNING_SECRET: process.env.CONTENTFUL_SIGNING_SECRET,
  TENOVOS_API_URL: process.env.TENOVOS_API_URL,
  TENOVOS_API_KEY: process.env.TENOVOS_API_KEY,
  TENOVOS_CLIENT_ID: process.env.TENOVOS_CLIENT_ID,
  TENOVOS_USER: process.env.TENOVOS_USER,
  TENOVOS_PASSWORD: process.env.TENOVOS_PASSWORD,
});

export type EnvironmentProfile = 'base' | 'editor';

type SecretsByProfile = {
  base: z.infer<typeof baseSecretsSchema>;
  editor: z.infer<typeof editorSecretsSchema>;
};

export type SecretsForProfile<Profile extends EnvironmentProfile = 'base'> =
  SecretsByProfile[Profile];

/** Returns secrets for the selected application profile. */
export const getSecrets = <Profile extends EnvironmentProfile = 'base'>(
  profile: Profile = 'base' as Profile,
): SecretsForProfile<Profile> => {
  const secrets =
    profile === 'editor'
      ? editorSecretsSchema.parse(getEditorSecretValues())
      : baseSecretsSchema.parse(getBaseSecretValues());

  return secrets as SecretsForProfile<Profile>;
};

/**
 * Public configuration that can be shared with the browser.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file.
 */
export const Sites = z.enum(['skippy', 'compex', 'my', 'manage']);
const basePublicConfigSchema = z.object({
  NEXT_PUBLIC_SITE_ID: Sites.default('skippy').describe(
    'For multisite apps, which site to use.',
  ),
  NEXT_PUBLIC_BASE_URL_AUTH: z
    .string()
    .url()
    .describe('The url of the Auth app.'),
  NEXT_PUBLIC_BASE_URL_CART: z
    .string()
    .url()
    .describe('The url of the Cart app.'),
  NEXT_PUBLIC_BASE_URL_CHECKOUT: z
    .string()
    .url()
    .describe('The url of the Checkout app.'),
  NEXT_PUBLIC_BASE_URL_DOTCOM: z
    .string()
    .url()
    .describe(
      'The url of the Compassion website (allowing us to cutover from legacy to skippy at the right time).',
    ),
  NEXT_PUBLIC_BASE_URL_PROXY_API: z
    .string()
    .url()
    .describe('The url of the proxy API.'),
  NEXT_PUBLIC_GTM_ID: z
    .string()
    .optional()
    .describe('The id for Google Tag Manager.'),
  NEXT_PUBLIC_SKIPPY_SEARCH_API_URL: z
    .string()
    .url()
    .optional()
    .describe('The url of the Skippy Search API.'),
  NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY: z
    .string()
    .optional()
    .describe('The key for the Skippy Search API.'),
  NEXT_PUBLIC_GTM_CONTAINER: z
    .string()
    .optional()
    .describe('The container ID for Google Tag Manager.'),
  NEXT_PUBLIC_GTM_AUTH: z
    .string()
    .optional()
    .describe('The "gtm_auth" param for Google Tag Manager.'),
  NEXT_PUBLIC_GTM_PREVIEW: z
    .string()
    .optional()
    .describe('The "gtm_preview" param for Google Tag Manager.'),
  NEXT_PUBLIC_BASE_URL_MANAGE: z
    .string()
    .optional()
    .default('https://manage.compassion.com')
    .describe('The URL for the Manage app.'),
  NEXT_PUBLIC_BASE_URL_MY: z
    .string()
    .optional()
    .describe('The URL for My Compassion (New).'),
  NEXT_PUBLIC_BASE_URL_MYACCOUNT: z
    .string()
    .optional()
    .describe('The URL for My Account (legacy).'),
  NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD: z
    .string()
    .optional()
    .describe('The URL resetting a forgotten password.'),
  NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT: z
    .string()
    .optional()
    .describe('The URL to create an account.'),
  NEXT_PUBLIC_BASE_URL_WRITE: z
    .string()
    .optional()
    .describe('The url of the Letter Writing app.'),
  NEXT_PUBLIC_BASE_URL_COMPEX: z
    .string()
    .optional()
    .describe('The url of the Compassion Experience site.'),
  NEXT_PUBLIC_STAGE: z
    .string()
    .optional()
    .describe('The environment. prod, stage, devint, local.'),
  NEXT_PUBLIC_DOUBLE_THE_DONATION: z
    .string()
    .optional()
    .describe('Matching gifts double the donation form.'),
  NEXT_PUBLIC_DONOR_ADVISED_FUND: z
    .string()
    .optional()
    .describe('Donor Advised Fund Widget'),
  NEXT_PUBLIC_CARS_CID: z.string().optional().describe('Donate A Car Widget'),
  NEXT_PUBLIC_CARS_CCD: z.string().optional().describe('Donate A Car Widget'),
  NEXT_PUBLIC_GOOGLE_MAPS_KEY: z
    .string()
    .optional()
    .describe('The key for the google maps api.'),
});

const editorPublicConfigSchema = basePublicConfigSchema.extend({
  NEXT_PUBLIC_BASE_URL_SDG_MEDIA: z
    .string()
    .url()
    .optional()
    .describe('The url of the sdg media app'),
  NEXT_PUBLIC_BASE_URL_STORYBOOK: z
    .string()
    .url()
    .default('https://ark.devint.compassion.com/')
    .describe('The url of Storybook for the docs.'),
  NEXT_PUBLIC_CONNECT_PROXY_URL: z
    .string()
    .url()
    .optional()
    .describe('The url of the API for the Connect Proxy.'),
  NEXT_PUBLIC_CONNECT_PROXY_KEY: z
    .string()
    .optional()
    .describe('The Client ID for the Connect Proxy API.'),
  NEXT_PUBLIC_EDITOR_API_URL: z.string().optional(),
  NEXT_PUBLIC_ADS_DOMAIN: z
    .url()
    .optional()
    .describe('The url for the ads (aka banners) app.'),
});

// Next.js requires direct process.env.KEY access to inline public values.
const getBasePublicConfigValues = () => ({
  NEXT_PUBLIC_SITE_ID: process.env.NEXT_PUBLIC_SITE_ID,
  NEXT_PUBLIC_BASE_URL_AUTH: process.env.NEXT_PUBLIC_BASE_URL_AUTH,
  NEXT_PUBLIC_BASE_URL_CART: process.env.NEXT_PUBLIC_BASE_URL_CART,
  NEXT_PUBLIC_BASE_URL_CHECKOUT: process.env.NEXT_PUBLIC_BASE_URL_CHECKOUT,
  NEXT_PUBLIC_BASE_URL_DOTCOM: process.env.NEXT_PUBLIC_BASE_URL_DOTCOM,
  NEXT_PUBLIC_BASE_URL_PROXY_API: process.env.NEXT_PUBLIC_BASE_URL_PROXY_API,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_SKIPPY_SEARCH_API_URL:
    process.env.NEXT_PUBLIC_SKIPPY_SEARCH_API_URL,
  NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY:
    process.env.NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
  NEXT_PUBLIC_GTM_CONTAINER: process.env.NEXT_PUBLIC_GTM_CONTAINER,
  NEXT_PUBLIC_GTM_AUTH: process.env.NEXT_PUBLIC_GTM_AUTH,
  NEXT_PUBLIC_GTM_PREVIEW: process.env.NEXT_PUBLIC_GTM_PREVIEW,
  NEXT_PUBLIC_BASE_URL_MYACCOUNT: process.env.NEXT_PUBLIC_BASE_URL_MYACCOUNT,
  NEXT_PUBLIC_BASE_URL_MANAGE: process.env.NEXT_PUBLIC_BASE_URL_MANAGE,
  NEXT_PUBLIC_BASE_URL_MY: process.env.NEXT_PUBLIC_BASE_URL_MY,
  NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD:
    process.env.NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD,
  NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT:
    process.env.NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT,
  NEXT_PUBLIC_BASE_URL_WRITE: process.env.NEXT_PUBLIC_BASE_URL_WRITE,
  NEXT_PUBLIC_BASE_URL_COMPEX: process.env.NEXT_PUBLIC_BASE_URL_COMPEX,
  NEXT_PUBLIC_STAGE: process.env.NEXT_PUBLIC_STAGE,
  NEXT_PUBLIC_DOUBLE_THE_DONATION: process.env.NEXT_PUBLIC_DOUBLE_THE_DONATION,
  NEXT_PUBLIC_DONOR_ADVISED_FUND: process.env.NEXT_PUBLIC_DONOR_ADVISED_FUND,
  NEXT_PUBLIC_CARS_CID: process.env.NEXT_PUBLIC_CARS_CID,
  NEXT_PUBLIC_CARS_CCD: process.env.NEXT_PUBLIC_CARS_CCD,
  NEXT_PUBLIC_GOOGLE_MAPS_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
});

const getEditorPublicConfigValues = () => ({
  ...getBasePublicConfigValues(),
  NEXT_PUBLIC_BASE_URL_SDG_MEDIA: process.env.NEXT_PUBLIC_BASE_URL_SDG_MEDIA,
  NEXT_PUBLIC_BASE_URL_STORYBOOK: process.env.NEXT_PUBLIC_BASE_URL_STORYBOOK,
  NEXT_PUBLIC_CONNECT_PROXY_URL: process.env.NEXT_PUBLIC_CONNECT_PROXY_URL,
  NEXT_PUBLIC_CONNECT_PROXY_KEY: process.env.NEXT_PUBLIC_CONNECT_PROXY_KEY,
  NEXT_PUBLIC_EDITOR_API_URL: process.env.NEXT_PUBLIC_EDITOR_API_URL,
  NEXT_PUBLIC_ADS_DOMAIN: process.env.NEXT_PUBLIC_ADS_DOMAIN,
});

type PublicConfigByProfile = {
  base: z.infer<typeof basePublicConfigSchema>;
  editor: z.infer<typeof editorPublicConfigSchema>;
};

export type PublicConfigForProfile<
  Profile extends EnvironmentProfile = 'base',
> = PublicConfigByProfile[Profile];

/** Returns public configuration for the selected application profile. */
export const getPublicConfig = <Profile extends EnvironmentProfile = 'base'>(
  profile: Profile = 'base' as Profile,
): PublicConfigForProfile<Profile> => {
  const publicConfig =
    profile === 'editor'
      ? editorPublicConfigSchema.parse(getEditorPublicConfigValues())
      : basePublicConfigSchema.parse(getBasePublicConfigValues());

  return publicConfig as PublicConfigForProfile<Profile>;
};

/**
 * Feature Flags should be short lived. Once the feature is fully rolled out, the flag, and matching logic, should be removed.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file.
 * All feature flags should be booleans with a default of false. Since env vars are strings, we transform them to booleans.
 */
const featureFlagSchema = z.object({
  enableEditorCampaigns: stringToBool,
  enableFormBuilder: stringToBool,
  enableHeaderCartDrawer: stringToBool,
  enableMyContentFeed: stringToBool,
  enableMyContentFeedReactions: stringToBool,
  enableRedirects: stringToBool,
  enableSkippySearch: stringToBool,
  enableSocialProofToast: stringToBool,
  launchMyCompassion: stringToBool,
  disableFieldsOnPublish: stringToBool,
  disableThirdPartyScripts: stringToBool,
});
export const getFeatureFlags = () => {
  return featureFlagSchema.parse({
    enableEditorCampaigns: process.env.NEXT_PUBLIC_FF_enableEditorCampaigns,
    enableFormBuilder: process.env.NEXT_PUBLIC_FF_enableFormBuilder,
    enableHeaderCartDrawer: process.env.NEXT_PUBLIC_FF_enableHeaderCartDrawer,
    enableMyContentFeed: process.env.NEXT_PUBLIC_FF_enableMyContentFeed,
    enableMyContentFeedReactions:
      process.env.NEXT_PUBLIC_FF_enableMyContentFeedReactions,
    enableRedirects: process.env.NEXT_PUBLIC_FF_enableRedirects,
    enableSkippySearch: process.env.NEXT_PUBLIC_FF_enableSkippySearch,
    enableSocialProofToast: process.env.NEXT_PUBLIC_FF_enableSocialProofToast,
    launchMyCompassion: process.env.NEXT_PUBLIC_FF_launchMyCompassion,
    disableFieldsOnPublish: process.env.NEXT_PUBLIC_FF_disableFieldsOnPublish,
    disableThirdPartyScripts:
      process.env.NEXT_PUBLIC_FF_disableThirdPartyScripts,
  });
};

/**
 * Ensure the environment variables match what we expect. Missing variables will throw.
 * We return all env variables back as strings, so they can be passed on to the SST deploy.
 *
 * @deprecated Use `getServerEnvironment()` instead for the SST `environment` block.
 * This function includes empty strings for undefined optional vars, wasting Lambda env space.
 */
export const validateEnvironmentVariables = (
  profile: EnvironmentProfile = 'base',
) => {
  return envVarsSchema.parse({
    ...getSecrets(profile),
    ...getPublicConfig(profile),
    ...getFeatureFlags(),
  });
};

/**
 * Validates the selected environment profile and returns only variables with
 * actual values. Filters out undefined/empty optional vars to stay within the
 * AWS Lambda 4KB environment variable limit.
 *
 * Use this in the SST `environment` block instead of
 * `validateEnvironmentVariables()`.
 */
export const getServerEnvironment = (profile: EnvironmentProfile = 'base') => {
  const secrets = getSecrets(profile);
  const publicConfig = getPublicConfig(profile);
  getFeatureFlags(); // validate only — flags are inlined at build time via NEXT_PUBLIC_FF_* env vars

  // Only include vars that have actual values — skip undefined/empty strings.
  const all: Record<string, unknown> = { ...secrets, ...publicConfig };
  const filtered: Record<string, string> = {};
  for (const [key, value] of Object.entries(all)) {
    if (value !== undefined && value !== null && value !== '') {
      filtered[key] = String(value);
    }
  }
  return filtered;
};

/**
 * Static configuration that does not need to be controlled per-environment.
 */
export const generalConfig = {
  // If we have more pages than this, they will render on-demand instead of at build time.
  // Non-production builds use a smaller limit to speed up dev builds and reduce API pressure.
  maxStaticPagesToBuild:
    process.env.NEXT_PUBLIC_STAGE === 'prod' ||
    process.env.NEXT_PUBLIC_STAGE === 'stage'
      ? 50_000
      : 50,
  // Sitemaps cap at 50k as per https://developers.google.com/search/docs/crawling-indexing/sitemaps/build-sitemap#general-guidelines.
  singleSitemapLimit: 50_000,
  maxContentfulRetries: 8,
  defaultFundId: 'MN',
  defaultFundTridionId: 'tcm:5-102864',
  catalogFilterMin: 5,
  catalogFilterMax: 2000,
  catalogPerPage: 9,
  sacMinAge: 1,
  sacMaxAge: 22,
  redirectFallbacks: {
    cintl: 'https://www.compassion.com/sponsor-a-child/',
    blog: 'https://www.compassion.com/blog/',
  },
  cart: {
    transactionMax: 600_000,
  },
  donation: {
    amountMin: 5,
    amountMax: 600_000,
    optionsMin: 2,
    optionsMax: 5,
  },
  rate: 43,
  publisher: {
    name: 'Compassion International',
    url: 'https://www.compassion.com/',
    logoUrl: 'https://www.compassion.com/compassion-logo.png',
  },
  domain: {
    compassion: 'https://www.compassion.com',
  },
  socials: {
    facebook: 'https://www.facebook.com/compassionintl',
    twitter: 'https://x.com/compassion',
    instagram: 'https://www.instagram.com/compassion/#',
    youtube: 'https://www.youtube.com/user/CompassionIntl',
    linkedIn: 'https://linkedin.com/company/compassion-international',
    pinterest: 'https://www.pinterest.com/compassion/',
    wikipedia: 'https://en.wikipedia.org/wiki/Compassion_International',
  },
  contact: {
    telephone: '(800) 336-7676',
    areaServed: 'United States',
    countryCode: 'US',
  },
  address: {
    streetAddress: '12290 Voyager Pkwy',
    addressLocality: 'Colorado Springs',
    addressRegion: 'CO',
    postalCode: '80921',
    addressCountry: 'US',
  },
  images: {
    homepage: 'https://www.compassion.com/compassion-logo.png',
  },
  sitemap: {
    minArticlesForTag: 3,
  },
  leadForms: {
    firstNameMaxLength: 40,
    lastNameMaxLength: 80,
    combinedMaxLength: 100,
    churchNameMaxLength: 40,
    churchCityMaxLength: 40,
    //combinedMaxLength - lastNameMaxLength
    minMaxFirst: 20,
    //combinedMaxLength - firstNameMaxLength
    minMaxLast: 60,
  },
} as const;
